import React from 'react';
import request from 'superagent';
import { Reel } from '../reel/reel';
import '../ff001-starstruck-lines/ff001-starstruck.css';
import './style.css';
import { blank, cellsFor, clone, coinId, Config, findTrigger, forceCoins, isEditable, key,
  hasSpecial, naturalTrigger, toPlayQueue, fromPlayQueue, project, projectBoost, Scenario, symbols, wedges, windowFor } from './helpers';

type Preset = { _id: string; scenarios: Scenario[]; gameInput?: Record<string, unknown> };
const message = (error: any) => error?.response?.body?.error?.message || error?.message || 'Request failed';
const call = async (action: string, body: Record<string, unknown>) => {
  const res = await request.post(`${process.env.REACT_APP_API_URL}/n/slots/scenario/${action}`).withCredentials().send(body);
  if (!res.body?.success && !(action === 'get-config' && res.body?.config)) {
    throw new Error(res.body?.error?.message || res.body?.message || 'Request failed');
  }
  return res.body;
};

export const Model = ({ modelId }: { modelId: string }) => {
  const [config, setConfig] = React.useState<Config>();
  const [queue, setQueue] = React.useState<Scenario[]>(blank);
  const [presets, setPresets] = React.useState<Preset[]>([]);
  const [selected, setSelected] = React.useState('');
  const [unsupported, setUnsupported] = React.useState(false);
  const [userId, setUserId] = React.useState(localStorage.getItem('userid') || '');
  const [error, setError] = React.useState('');
  const [notice, setNotice] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  React.useEffect(() => {
    let cancelled = false;
    setConfig(undefined); setQueue(blank()); setSelected(''); setUnsupported(false); setError('');
    Promise.all([call('get-config', { modelId }), call('get', { modelId })]).then(([c, p]) => {
      if (!cancelled) { setConfig(c.config); setPresets(p.scenarios || []); }
    }).catch(e => { if (!cancelled) setError(message(e)); });
    return () => { cancelled = true; };
  }, [modelId]);
  if (!config) return <div role={error ? 'alert' : 'status'}>{error || 'Loading'}</div>;
  const base = queue[0];
  const editable = !unsupported && isEditable(queue, config);
  const projection = editable ? project(queue, config) : { steps: [], error: 'This preset cannot be edited safely. Its original JSON is preserved; select New to create a supported queue.' };
  const window = editable ? windowFor(base, config) : [];
  const triggered = editable && (naturalTrigger(window) || (base.randomBonus === 'activate' && hasSpecial(window)));
  let boost: ReturnType<typeof projectBoost> | undefined;
  if (editable && base.wheel && wedges[base.wheel.wedgeIndex] === 'boost') {
    try { boost = projectBoost(base, config); } catch { /* Projection displays invalid scenario errors. */ }
  }
  const changeBase = (next: Scenario) => {
    setQueue([next]); setError(''); setNotice(queue.length > 1 ? 'Base changed; rebuild the respin queue for this entry.' : '');
  };
  const run = async (action: () => Promise<void>) => {
    setBusy(true); setError(''); setNotice('');
    try { await action(); } catch (e) { setError(message(e)); } finally { setBusy(false); }
  };
  const coinOptions = config.coinFeature.coins.map(coin => <option key={coinId(coin)} value={coinId(coin)}>
    {coin.coinType === 'price' ? `${coin.multiplier}× total bet` : coin.coinType.toUpperCase()}
  </option>);
  const changeRespin = (index: number, cell: { reel: number; row: number }, value: string) => {
    const placements = queue[index].respin!.filter(p => key(p) !== key(cell));
    if (value) placements.push(value === 'collect' ? { ...cell, collector: true }
      : { ...cell, coin: clone(config.coinFeature.coins.find(c => coinId(c) === value)!) });
    // Later cards may reference a Collect that no longer exists; rebuild them explicitly.
    setQueue([...queue.slice(0, index), { respin: placements.sort((a, b) => a.reel - b.reel || a.row - b.row) }]);
    setNotice(index < queue.length - 1 ? 'Respin changed; later steps were removed.' : '');
  };
  const playQueue = unsupported ? queue : toPlayQueue(queue, config);
  const payload = { modelId, scenarios: playQueue };
  return <main className="ff001-container ff038-editor"><fieldset disabled={busy} className="ff038-form">
    <h1>GOLDEN GOAT SUPER WHEEL</h1>
    <label>Saved scenario <select aria-label="Saved scenario" value={selected} onChange={e => {
      setSelected(e.target.value); setNotice(''); setError('');
      const preset = presets.find(p => p._id === e.target.value);
      const normalized = preset ? fromPlayQueue(preset.scenarios) : blank();
      const supported = isEditable(normalized, config) && (!preset?.gameInput || Object.keys(preset.gameInput).length === 0);
      setQueue(supported ? normalized : clone(preset!.scenarios));
      setUnsupported(!supported);
    }}><option value="">New scenario</option>{presets.map(p => <option key={p._id} value={p._id}>{p._id}</option>)}</select></label>
    {editable && <>
      <section>
        <h2>Base spin</h2>
        <button onClick={() => { const next = findTrigger(config); next ? changeBase(next) : setError('No natural trigger exists on these reels.'); }}>Find natural trigger</button>
        <button onClick={() => { const next = findTrigger(config, true); next ? changeBase(next) : setError('No random-entry candidate exists on these reels.'); }}>Find random entry</button>
        <label><input type="checkbox" aria-label="Custom symbols" checked={!!base.window} onChange={e => changeBase(e.target.checked
          ? { ...base, reelIndexes: undefined, window: clone(window) }
          : { reelIndexes: [0, 0, 0], randomBonus: 'skip' })} />Custom symbols</label>
        {!base.window && <div className="reelContainer">{(base.reelIndexes ?? [0, 0, 0]).map((stop, reel) => <Reel
          key={reel} index={reel} rows={3} reelIndex={stop} reelStrips={config.baseSpinsReels[reel]}
          setReelIndex={({ dir, forcedIndex }) => {
            const length = config.baseSpinsReels[reel].length;
            const next = ((forcedIndex ?? stop + (dir || 1)) % length + length) % length;
            changeBase({ reelIndexes: (base.reelIndexes ?? [0, 0, 0]).map((s, r) => r === reel ? next : s), randomBonus: 'skip' });
          }} />)}</div>}
        <div className="ff038-board" aria-label="Base board">{window.map((column, reel) => <div key={reel}>
          {column.map((symbol, row) => <div className="ff038-cell" key={row}>
            <span>R{reel + 1} · {row + 1}</span>
            {base.window ? <select aria-label={`Symbol ${reel}-${row}`} value={symbol} onChange={e => {
              const next = clone(base.window!); next[reel][row] = e.target.value;
              changeBase({ window: next, randomBonus: 'skip' });
            }}>{[...symbols, reel === 1 ? 'collect' : 'coin'].map(s => <option key={s}>{s}</option>)}</select> : <strong>{symbol}</strong>}
            {symbol === 'coin' && <select aria-label={`Coin ${reel}-${row}`}
              value={base.coins?.find(c => c.reel === reel && c.row === row) ? coinId(base.coins.find(c => c.reel === reel && c.row === row)!.coin) : ''}
              onChange={e => {
                const coins = (base.coins ?? []).filter(c => c.reel !== reel || c.row !== row);
                if (e.target.value) coins.push({ reel, row, coin: clone(config.coinFeature.coins.find(c => coinId(c) === e.target.value)!) });
                changeBase({ ...base, coins });
              }}><option value="">Random value</option>{coinOptions}</select>}
          </div>)}
        </div>)}</div>
        <button onClick={() => changeBase(forceCoins(base, config))}>Set all Coin values</button>
        <label>Random entry <select aria-label="Random entry" value={base.randomBonus ?? 'skip'} onChange={e => {
          const next = { ...base, randomBonus: e.target.value as 'activate' | 'skip' };
          if (!naturalTrigger(window) && next.randomBonus === 'skip') delete next.wheel;
          changeBase(next);
        }}><option value="skip">Skip</option><option value="activate" disabled={!hasSpecial(window) || naturalTrigger(window)}>Force Coin rain</option></select></label>
        <p>{triggered ? 'Hold & Win entry' : 'Base spin; no bonus trigger'}</p>
      </section>
      {triggered && <section>
        <h2>Super Wheel</h2>
        <label>Wheel wedge <select aria-label="Wheel wedge" value={base.wheel?.wedgeIndex ?? ''} onChange={e => {
          const wedgeIndex = Number(e.target.value);
          changeBase({ ...base, wheel: e.target.value === '' ? undefined : { wedgeIndex } });
        }}><option value="">Random wheel</option>{wedges.map((prize, index) => <option key={index} value={index} disabled={config.wheel.weights[index] <= 0}>{index + 1} · {prize}</option>)}</select></label>
        {boost && <div>
          <p>BOOST adds up to three symbols, then uses any remaining actions for coin upgrades up to MINOR. A coin may be upgraded repeatedly.</p>
          {boost.actions.map((action, index) => <div key={index}>
            <label>BOOST action {index + 1} <select aria-label={`BOOST target ${index + 1}`} value={key(action)} onChange={e => {
              const next = boost!.choices[index].find(choice => key(choice) === e.target.value)!;
              changeBase({ ...base, wheel: { ...base.wheel!, boostActions: [...boost!.actions.slice(0, index), clone(next)] } });
            }}>{boost!.choices[index].map(choice => <option key={key(choice)} value={key(choice)}>
              {'upgrade' in choice ? 'Upgrade coin' : 'collector' in choice ? 'Add Collect' : 'Add coin'} · R{choice.reel + 1} · {choice.row + 1}
            </option>)}</select></label>
            {'coin' in action && <label>Prize <select aria-label={`BOOST coin ${index + 1}`} value={coinId(action.coin)} onChange={e => {
              const next = { reel: action.reel, row: action.row, coin: clone(config.coinFeature.coins.find(c => coinId(c) === e.target.value)!) };
              changeBase({ ...base, wheel: { ...base.wheel!, boostActions: [...boost!.actions.slice(0, index), next] } });
            }}>{coinOptions}</select></label>}
          </div>)}
          {!boost.actions.length && <p>No legal BOOST actions remain; existing prizes are preserved.</p>}
        </div>}
        <h2>Respin queue</h2>
        <p>Collects stay; outer Coins clear after each collection. New Coins or Collects reset to three respins. Pin the entry values to script subsequent spins. The queue stops at the win cap or respin exhaustion.</p>
        {queue.slice(1).map((step, i) => {
          const index = i + 1;
          const held = projection.steps[index - 1]?.collectors ?? [];
          return <fieldset key={index}><legend>Respin {index} · {projection.steps[index]?.terminal ? projection.steps[index]?.terminalReason : `${projection.steps[index]?.respins ?? '?'} remaining`}</legend>
            <div className="ff038-board">{[0, 1, 2].map(reel => <div key={reel}>{[0, 1, 2].map(row => {
              const cell = { reel, row }; const prior = held.some(c => key(c) === key(cell));
              const p = step.respin!.find(p => key(p) === key(cell));
              return <label className="ff038-cell" key={row}>R{reel + 1} · {row + 1}
                {prior ? <strong>Held Collect</strong> : <select aria-label={`Respin ${index} cell ${reel}-${row}`}
                  value={p ? 'coin' in p ? coinId(p.coin) : 'collect' : ''} onChange={e => changeRespin(index, cell, e.target.value)}>
                  <option value="">Empty</option>{reel === 1 ? <option value="collect">Collect</option> : coinOptions}
                </select>}
              </label>;
            })}</div>)}</div>
          </fieldset>;
        })}
        <button disabled={!base.wheel || !!projection.scriptHint || !!projection.error || projection.steps[projection.steps.length - 1]?.terminal || queue.length >= 256}
          onClick={() => setQueue([...queue, { respin: [] }])}>Add empty respin</button>
        <button disabled={queue.length < 2} onClick={() => setQueue(queue.slice(0, -1))}>Remove last respin</button>
      </section>}
    </>}
    {projection.scriptHint && <p>{projection.scriptHint}</p>}
    {projection.error && <p role="alert">{projection.error}</p>}
    <details><summary>Native scenario JSON</summary><textarea aria-label="Native scenario JSON" readOnly value={JSON.stringify(playQueue, null, 2)} /></details>
    <label>User Public Key <input aria-label="User Public Key" value={userId} onChange={e => {
      setUserId(e.target.value); localStorage.setItem('userid', e.target.value);
    }} /></label>
    {error && <p role="alert">{error}</p>}{notice && <p role="status">{notice}</p>}
    <div className="model-buttons">
      <button disabled={busy} onClick={() => { setQueue(blank()); setSelected(''); setUnsupported(false); setError(''); setNotice(''); }}>New</button>
      <button disabled={busy || !!projection.error} onClick={() => run(async () => {
        const res = await call('upsert', { ...payload, _id: selected || undefined });
        const id = res._id || selected;
        if (id) { setSelected(id); setPresets([...presets.filter(p => p._id !== id), { _id: id, scenarios: clone(playQueue) }]); }
        setNotice('Scenario saved');
      })}>Save</button>
      {selected && <button disabled={busy} onClick={() => {
        if (!globalThis.confirm('Delete this scenario?')) return;
        run(async () => { await call('delete', { modelId, _id: selected }); setPresets(presets.filter(p => p._id !== selected));
          setSelected(''); setQueue(blank()); setUnsupported(false); setNotice('Scenario deleted'); });
      }}>Delete</button>}
      <button disabled={busy || !!projection.error} onClick={() => {
        if (!userId.trim()) { setError('Enter a User Public Key.'); return; }
        run(async () => { await call('play-user', { ...payload, gameId: modelId, userPublicKey: userId.trim() }); setNotice('Scenario queued for play'); });
      }}>Play</button>
    </div>
  </fieldset></main>;
};
export default Model;
